<script>

import { mapGetters, mapState } from 'vuex';

const Flavors = {
  accountSettings: 'account-settings',
  subscriptionType: 'subscription-type',
  creditCard: 'credit-card',
  signupOffering: 'signup-offering',
  signup: 'signup',
  signupTimeline: 'signup-timeline',
  webOnly: 'web-only',
  applePromotion: 'apple-promotion',
};

export default {
  name: 'BenefitMixin',
  computed: {
    ...mapState('benefits', ['benefitId', 'benefitType', 'benefitAmount', 'benefitPercentage', 'redeemed']),
    ...mapGetters('benefits', [
      'BENEFIT_TYPES',
      'discountedPrice',
      'monthsText',
      'activeBenefitPricing',
      'declaresYearlyPlan',
      'benefitPeriodPrice',
      'listPriceForPeriod',
      'hasUnredeemedBenefit',
      'benefitBelongsToOtherPlan',
      'benefitEventProperties',
    ]),
    hasBenefitToShow() {
      return this.hasUnredeemedBenefit;
    },
    // Said out loud rather than hidden, which would look like picking annual lost the benefit
    planTransitionNote() {
      return this.benefitBelongsToOtherPlan ? 'ולאחר מכן יעבור למנוי שנתי' : '';
    },
    planTransitionSuffix() {
      return this.planTransitionNote ? `, ${this.planTransitionNote}` : '';
    },
    isGenericFreeMonthBenefit() {
      return this.benefitType === this.BENEFIT_TYPES.FREE_MONTHS && !this.redeemed;
    },
    isPercentageDiscountBenefit() {
      return this.benefitType === this.BENEFIT_TYPES.PERCENTAGE_DISCOUNT && !this.redeemed;
    },
    isFixedPriceBenefit() {
      return this.benefitType === this.BENEFIT_TYPES.FIXED_PRICE && !this.redeemed;
    },
    benefitPeriodString() {
      if (!this.declaresYearlyPlan) {
        return this.monthString;
      }
      if (this.benefitAmount === 1) {
        return 'השנה הראשונה';
      }
      if (this.benefitAmount === 2) {
        return 'השנתיים הראשונות';
      }
      return `${this.benefitAmount} השנים הראשונות`;
    },
    benefitSpecificText() {
      if (this.isFixedPriceBenefit && this.benefitPeriodPrice) {
        return `${this.benefitPeriodString} ב־${this.benefitPeriodPrice} ₪ במקום ${this.listPriceForPeriod} ₪`;
      }
      if (this.isPercentageDiscountBenefit && this.benefitPeriodPrice) {
        // A stated yearly plan is priced per year; a legacy 12-month one is quoted per month
        const price = this.declaresYearlyPlan
          ? this.benefitPeriodPrice
          : this.activeBenefitPricing?.monthlyPrice ?? this.benefitPeriodPrice;
        return `${this.benefitPeriodString} ב־${this.benefitPercentage}% הנחה (${price} ₪)`;
      }
      return this.benefitPeriodString;
    },
    applePromotionText() {
      return `מגיעה לך הטבה של ${this.numberOfMonthString} בחינם`;
    },
    monthString() {
      if (this.benefitAmount === 1) {
        return 'חודש ראשון';
      }
      if (this.benefitAmount === 2) {
        return 'חודשיים ראשונים';
      }
      return `${this.benefitAmount} חודשים ראשונים`;
    },
    numberOfMonthString() {
      if (this.benefitAmount === 1) {
        return 'חודש אחד';
      }
      if (this.benefitAmount === 2) {
        return 'חודשיים';
      }
      return `${this.benefitAmount} חודשים`;
    },
    moreMonthsString() {
      if (this.benefitAmount === 1) {
        return 'חודש נוסף';
      }
      if (this.benefitAmount === 2) {
        return 'חודשיים נוספים';
      }
      return `${this.benefitAmount} חודשים נוספים`;
    },
    // Keyed off the type rather than isFixedPriceBenefit, which also requires unredeemed:
    // PaymentFormPage renders the box without checking redeemed, and a redeemed fixed-price benefit
    // has no percentage to fall back on.
    benefitPricingClause() {
      if (this.benefitType === this.BENEFIT_TYPES.FIXED_PRICE) {
        return `ב-${this.benefitPeriodPrice} ₪ במקום ${this.listPriceForPeriod} ₪`;
      }
      const priceText = this.discountedPrice ? ` (${this.discountedPrice} ₪)` : '';
      return `ב-${this.benefitPercentage}% הנחה${priceText}`;
    },
    signupOfferingMonthsText() {
      return this.benefitAmount === 1 ? 'חודש ראשון' : `${this.benefitAmount}  חודשים ראשונים`;
    },
    percentageBenefitSignupOfferingText() {
      const periodText = this.declaresYearlyPlan ? this.benefitPeriodString : this.signupOfferingMonthsText;
      return `${periodText} ${this.benefitPricingClause} בנוסף לתקופת הנסיון.`;
    },
    percentageBenefitSignupTimelineText() {
      if (this.declaresYearlyPlan) {
        const verb = this.benefitAmount === 1 ? 'תהיה' : 'יהיו';
        return `בסיום תקופת הנסיון, ${this.benefitPeriodString} ${verb} ${this.benefitPricingClause}.`;
      }
      const monthsText = (this.benefitAmount === 1) ? 'חודש ראשון' : `${this.monthsText}  ראשונים`;
      return `בסיום תקופת הנסיון, יהיו לך ${monthsText} ${this.benefitPricingClause}.`;
    },
    flavors() {
      return Flavors;
    },
  },
};

</script>
